import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import { listCompanies } from "graphql/queries";
import SelectUser from "components/users/SelectUser";

const FarmForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [farmForm, setFarmForm] = useState(initialValues || {});
  const [companies, setCompanies] = useState({ list: [], loading: false });
  const [errors, setErrors] = useState({ name: "", companyID: "" });
  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(farmForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;

    if (!hasErrors) onSubmit(farmForm);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === farmForm[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const selectResponsible = (responsibleID) => {
    setFarmForm((prevSt) => ({
      ...prevSt,
      responsiblePersonId: responsibleID,
    }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFarmForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setFarmForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setFarmForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setFarmForm((prevSt) => ({ ...prevSt, [name]: value }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({ name: "", companyID: "" });
    setFarmForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me?.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me?.managedCompanies?.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [{ id: { contains: id } }, { name: { contains: name } }],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setFarmForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={farmForm.name || ""}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          />
          <TextField
            name="location"
            label={strings.fields.location.label}
            value={farmForm.location || ""}
            onChange={onChange}
          />
        </Grid>
        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          {type === "create" && (
            <Flex direction="column" gap="xs">
              <Label htmlFor="companyID">
                <LabelRequired text={strings.fields.company.label} />
              </Label>
              <Autocomplete
                id="companyID"
                name="companyID"
                isRequired
                placeholder={strings.fields.company.placeholder}
                value={
                  companies.list.find(
                    (companyRec) => companyRec.id === farmForm.companyID
                  )?.name ??
                  farmForm.companyID ??
                  ""
                }
                options={companies.list.map((companyRec) => ({
                  id: companyRec.id,
                  label: companyRec.name,
                }))}
                isLoading={companies.loading}
                onClear={() => onClearSelect("companyID")}
                onChange={onChangeSelect}
                onSelect={({ id }) => onSelect("companyID", id)}
                errorMessage={errors.companyID}
                hasError={!!errors.companyID}
                menuSlots={{ Empty: globalStrings.noResults }}
              />
            </Flex>
          )}
          <div>
            <Label style={{ display: "block", marginBottom: 6 }}>
              {strings.fields.responsible.label}
            </Label>
            <SelectUser
              value={farmForm.responsiblePersonId}
              placeholder={strings.fields.responsible.label}
              onSelect={selectResponsible}
            />
          </div>
        </Grid>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={farmForm.description || ""}
          onChange={onChange}
        />
        <Grid
          templateColumns={{ base: "1fr", large: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <TextField
            name="sizeM2"
            label={strings.fields.sizeM2.label}
            value={farmForm.sizeM2 || ""}
            onChange={onChange}
          />
          <TextField
            name="averageAltitudeM"
            label={strings.fields.averageAltitudeM.label}
            value={farmForm.averageAltitudeM || ""}
            onChange={onChange}
          />
        </Grid>
        <TextField
          name="photoEvidence"
          label={strings.fields.photoEvidence.label}
          placeholder="foto1.jpg, foto2.jpg"
          value={
            Array.isArray(farmForm.photoEvidence)
              ? farmForm.photoEvidence.join(", ")
              : farmForm.photoEvidence || ""
          }
          onChange={(e) =>
            setFarmForm({ ...farmForm, photoEvidence: e.target.value })
          }
        />
        <TextAreaField
          name="blueprints"
          label={strings.fields.blueprints.label}
          placeholder="Formato: fecha|nombre|archivo.pdf, ..."
          value={
            Array.isArray(farmForm.blueprints)
              ? farmForm.blueprints.join(", ")
              : farmForm.blueprints || ""
          }
          onChange={(e) =>
            setFarmForm({ ...farmForm, blueprints: e.target.value })
          }
        />
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          />
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          />
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: { label: "Nombre" },
      location: { label: "Ubicación" },
      sizeM2: { label: "Tamaño (m²)" },
      averageAltitudeM: { label: "Altitud promedio (m)" },
      responsible: { label: "Persona responsable" },
      photoEvidence: { label: "Evidencia fotográfica" },
      blueprints: { label: "Planos" },
      description: { label: "Descripción" },
      company: { label: "Empresa", placeholder: "Buscar empresa..." },
      buttons: { create: "Crear", save: "Guardar", reset: "Restablecer" },
    },
    validations: { incorrectValue: "Valor incorrecto" },
    responses: { errorFetchCompanies: "Error al consultar empresas" },
  },
});

export default FarmForm;
